import {GUIDE_FROM_LEARN, GUIDE_FROM_PARAM, GUIDE_TASK_PARAM} from '../../../guides/constants';
import {ALERTS_ROOT, ALERTS_RULES} from '../../alerts_center/alertsCenterPaths';
import {TASK_ALERTS, TASK_FIRST_VISIT, TASK_JOINED} from './learnModel';

/**
 * A dónde lleva el botón de cada tarea (contrato §5), confirmado contra `routes.jsx`. El backend
 * manda el cliente sugerido (`client_id`); las tareas que viven en una pantalla de cliente caen a
 * otra pantalla si la agencia no tiene clientes (`client_id: null`).
 *
 * El primer paso de cada guía de Aprende vive en ESTA pantalla, porque la guía se lanza navegando al
 * destino con `?guide=<slug>` (`start(slug)` no navega). Si un destino cambia, cambia con él la guía
 * de `apr-guias`: el mapa vive también en `~/.claude/tandas/2026-10-03-aprende-dashbo/apr-fe-pagina/destinos.md`.
 *
 * Decisiones que no se deducen de la tabla:
 * - La IA va a `/dashbo-mcp` y no al paso del túnel (`/onboarding/ia`): ése mide la primera consulta
 *   de la AGENCIA y a la segunda persona del equipo le diría que el paso «ya está dado».
 * - Las alertas, sin el Centro de Alertas, van al Panel y sin guía: `ALERTS_CONFIGURED` lo escribe la
 *   configuración de alertas de una LÍNEA. Con el Centro van a «Mis reglas» con la guía `aprende-alertas`
 *   (D3 de la tanda «Aprende y Centro de Alertas», 26/10; antes, C-08, a «Crear una alerta»): ahí la tarea
 *   se cumple con cualquier cambio que la persona guarda en una alerta, no sólo creando.
 * - El límite diario va al Panel: en `/client/:id/conections` la fila está en modo conexión y no
 *   ofrece el lápiz del límite; la card «Fuentes de datos» del Panel sí.
 * - El presupuesto va a las líneas del cliente (`/client/:id/lines`): en el listado de Clientes la
 *   celda es de sólo lectura; en las líneas, el monto abre el cajón de presupuesto y objetivo.
 */

const CLIENTS = '/agency/clients';

const clientPath = (suffix) => (clientId) => `/client/${encodeURIComponent(String(clientId))}${suffix}`;

// `path`: un string fijo o una función del cliente. `withoutClient`: a dónde va si la tarea pide un
// cliente y no hay ninguno (por defecto, el listado de clientes).
const DESTINATIONS = {
    [TASK_FIRST_VISIT]: {path: CLIENTS},
    budget_or_goal_adjusted: {path: clientPath('/lines')},
    assistant_connected: {path: '/dashbo-mcp'},
    first_client_query: {path: '/dashbo-mcp'},
    report_saved: {path: clientPath('/informes')},
    line_created: {path: clientPath('')},
    conversions_chosen: {path: clientPath('')},
    daily_limit_set: {path: clientPath('')},
    [TASK_ALERTS]: {path: clientPath('')},
    columns_configured: {path: '/marketing'},
    memory_added: {path: clientPath('/booster#alimenta'), withoutClient: '/ia-booster'},
    portal_invited: {path: clientPath('/portal-config')},
    report_shared_portal: {path: clientPath('/informes')},
    agency_prompt_used: {path: '/agency/prompts'},
    team_invited: {path: '/agency-team'},
    clients_assigned: {path: '/agency-team'},
    team_progress_reviewed: {path: '/agency-team'},
    agency_prompt_created: {path: '/agency/prompts'},
    media_plan_synced: {path: clientPath('/media-plan')},
    custom_metric_created: {path: '/agency/personalization/metrics'},
    source_linked: {path: clientPath('/conections')},
};

/**
 * Dónde arranca cada recorrido de primera visita (H-07): la pantalla de su disparo. Las dos variantes
 * de Clientes son `FIRST_VISIT_CLIENTS_GUIDE_SLUGS` del backend. El del Panel necesita un cliente: sin
 * `client_id` no tiene dónde arrancar. Un slug que no está acá no se lanza (no se sabe dónde empieza).
 * El del Centro de Alertas va en «Ahora» (D5 de la tanda del 26/10): el backend lo manda sólo a una agencia
 * con el Centro, después del Panel.
 */
const TOUR_PATHS = {
    'clientes-primera-visita': () => CLIENTS,
    'clientes-primera-visita-con-cartera': () => CLIENTS,
    'tablero-primera-visita': () => '/marketing',
    'panel-primera-visita': (clientId) => (clientId === null || clientId === undefined ? null : clientPath('')(clientId)),
    'centro-alertas-intro': () => ALERTS_ROOT,
};

/**
 * Suma `?guide=<slug>` a una ruta, ANTES del `#` si lo tiene (`/client/1/booster#alimenta`). Con la
 * tarea (`fromTask`), suma también de dónde viene la guía: al terminarla se ofrece volver a Aprende con
 * la tarea que sigue (C-03).
 */
export function withGuide(path, slug, fromTask = null) {
    if (!path || !slug) return path;
    const hashAt = path.indexOf('#');
    const base = hashAt === -1 ? path : path.slice(0, hashAt);
    const hash = hashAt === -1 ? '' : path.slice(hashAt);
    const separator = base.includes('?') ? '&' : '?';
    const from = fromTask
        ? `&${GUIDE_FROM_PARAM}=${GUIDE_FROM_LEARN}&${GUIDE_TASK_PARAM}=${encodeURIComponent(fromTask)}`
        : '';
    return `${base}${separator}guide=${encodeURIComponent(slug)}${from}${hash}`;
}

function basePath(task) {
    const def = task && DESTINATIONS[task.key];
    if (!def) return null;
    if (task.key === TASK_ALERTS && task.alertsCenter) return ALERTS_RULES;
    if (typeof def.path === 'string') return def.path;
    if (task.clientId === null || task.clientId === undefined) return def.withoutClient || CLIENTS;
    return def.path(task.clientId);
}

/** El destino de la tarea SIN guía, o `null` si no tiene a dónde ir (`joined_agency`). */
export function destinationPath(task) {
    if (!task || task.key === TASK_JOINED) return null;
    return basePath(task);
}

/**
 * El recorrido que lanza «Haz el recorrido» (H-07): el primero que le falta a la persona y se puede
 * lanzar, en el orden que manda el backend (Clientes → Tablero → Panel). Ya no siempre el de Clientes:
 * a Pablo le relanzaba el que acababa de ver, con dos pendientes.
 * - Backend viejo (sin `guide_slugs`): el de Clientes (`guide_slug`), como antes.
 * - No falta ninguno (la tarea está hecha): el de Clientes, para repasar.
 * - Faltan, pero ninguno se puede lanzar (el Panel sin cliente): `null`, y el botón lleva a Clientes.
 */
export function tourGuide(task) {
    if (!task || task.key !== TASK_FIRST_VISIT) return null;
    const clientsGuide = task.guideSlug ? {slug: task.guideSlug, path: CLIENTS} : null;
    if (!Array.isArray(task.guideSlugs)) return clientsGuide;
    if (!task.guideSlugs.length) return clientsGuide;
    for (const slug of task.guideSlugs) {
        const pathOf = TOUR_PATHS[slug];
        const path = pathOf ? pathOf(task.clientId) : null;
        if (path) return {slug, path};
    }
    return null;
}

/**
 * La guía de una tarea, `{slug, path}` con la pantalla donde arranca, o `null` si no tiene. Sólo si el
 * backend mandó `guide_slug` (lo manda sólo si la persona puede verla). Sin cliente, la guía de una
 * pantalla de cliente arrancaría en el listado y sólo podría decir «esta guía sigue en otra pantalla»:
 * no guía nada, así que no se ofrece.
 */
export function taskGuide(task) {
    if (!task || task.key === TASK_JOINED) return null;
    if (task.key === TASK_FIRST_VISIT) return tourGuide(task);
    if (!task.guideSlug) return null;
    // La guía de las alertas vive en «Mis reglas» del Centro (D3) y no pide cliente. Sin el Centro no se ofrece
    // aunque el backend la mande (el anterior a la tanda la manda a todos): mandaría a una pantalla que no existe.
    if (task.key === TASK_ALERTS) return task.alertsCenter ? {slug: task.guideSlug, path: ALERTS_RULES} : null;
    const def = DESTINATIONS[task.key];
    const needsClient = def && typeof def.path === 'function';
    if (needsClient && (task.clientId === null || task.clientId === undefined)) return null;
    const path = basePath(task);
    return path ? {slug: task.guideSlug, path} : null;
}

/**
 * Lo que hace el ÚNICO botón de la tarea (C-04: «Hacerlo ahora» y «Mostrarme cómo» iban a la misma
 * pantalla y el usuario tenía que decidir de antemano si iba a necesitar ayuda). `{to, guided}`:
 * - con guía y la primera vez (`guideSeen` falso): la pantalla con la guía, que se cierra con la ×;
 * - después, o sin guía: sólo la pantalla;
 * - el recorrido de primera visita ES la guía: lanza siempre la que falta.
 * `null` si la tarea no tiene a dónde ir.
 */
export function startTarget(task, {guideSeen = false} = {}) {
    const path = destinationPath(task);
    if (!path) return null;
    const guide = taskGuide(task);
    if (guide && (task.key === TASK_FIRST_VISIT || !guideSeen)) {
        return {to: withGuide(guide.path, guide.slug, task.key), guided: true};
    }
    return {to: path, guided: false};
}
